import type { Router } from 'vue-router'
import { useAuthStore, useUserStore } from '@/store'
import { getPassport } from '@/api/chat'

export async function setupPageGuard(router: Router) {
  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()
    const userStore = useUserStore()
    const token = to.query?.token_next_school as string
    const secretToken = JSON.parse(localStorage.getItem('SECRET_TOKEN') as string)?.data

    const authRoutes = ['login-teacher', 'login-student', 'login-parent', 'sign-up', 'sign-up-student', 'SignUp', 'forgot-password', 'forgot-password-student', 'maintenance', 'welcome', 'share-chat', 'embed-chat']

    const hasVisited = localStorage.getItem('HAS_VISITED')

    // Helper function to get appropriate login route based on role
    const getLoginRoute = () => {
      const role = userStore.role?.code || userStore.role
      return role === 'STUDENT' ? 'login-student' : 'login-teacher'
    }

    // Helper to check token expiry
    const checkTokenExpired = (bearerToken: string) => {
      try {
        if (!bearerToken)
          return true

        const payloadBase64 = bearerToken?.split('.')[1]
        if (!payloadBase64)
          return true

        const base64 = payloadBase64
          .replace(/-/g, '+')
          .replace(/_/g, '/')
          .padEnd(payloadBase64.length + (4 - payloadBase64.length % 4) % 4, '=')

        const payload = JSON.parse(atob(base64))
        const now = Date.now() / 1000
        // console.log('token expired check:', payload.exp < now)
        return payload.exp && payload.exp < now
      }
      catch (e) {
        return true
      }
    }

    // Allow share-chat and embed-chat routes to bypass auth checks (for embedded iframes)
    const isEmbeddedRoute = to.name === 'share-chat' || to.name === 'embed-chat'

    // Public embedded routes - allow access without any auth checks
    if (isEmbeddedRoute) {
      const appCode = to.params.appCode as string

      // Try to get passport token, but don't block if it fails (public chat)
      if (appCode && (appCode !== authStore.appCode || !authStore.token_pass_port)) {
        try {
          const res = await getPassport(appCode)
          authStore.setTokenPassPort(res?.data?.access_token)
        }
        catch (error: any) {
          // Don't redirect to login for public embedded chats
          console.warn('Failed to get passport token for embedded chat:', error)
        }
      }

      if (appCode && !authStore.appCode)
        authStore.setAppCode(appCode)

      next()
      return
    }

    if (!secretToken) {
      if (!authRoutes.includes(to.name as string)) {
        if (!hasVisited && import.meta.env.VITE_ENABLE_WELCOME_REDIRECT === 'true') {
          window.location.href = 'https://welcome.nextschool.app'
          return
        }
        else {
          next({ name: getLoginRoute() })
          return
        }
      }
    }
    else {
      // Check if token is expired
      if (checkTokenExpired(secretToken)) {
        if (!authRoutes.includes(to.name as string)) {
          // Import dynamically to avoid circular dependency issues if any, though direct import is usually fine
          const { showSessionExpiredModal } = await import('@/utils/request/axios')
          showSessionExpiredModal()
          next({ name: getLoginRoute() })
          return
        }
      }
    }

    if (to.name === 'Workspace' && !!token)
      authStore.setToken(token)

    if (to.name === 'share-chat' || to.name === 'embed-chat') {
      const appCode = to.params.appCode as string

      if (appCode !== authStore.appCode || !authStore.token_pass_port) {
        try {
          const res = await getPassport(appCode)
          authStore.setTokenPassPort(res?.data?.access_token)
        }
        catch (error: any) {
          next({ name: 'login-teacher' })
        }
      }

      if (!authStore.appCode)
        authStore.setAppCode(appCode)
    }

    // Route-level role guard
    if ((to.meta as any)?.requiresAdmin && userStore.role !== 'ADMIN') {
      // Redirect non-admin to a safe page
      next({ name: 'classroom.teacher.list' })
      return
    }

    if (!hasVisited)
      localStorage.setItem('HAS_VISITED', 'true')

    next()
  })
}
